import { type ComponentProps } from 'react';
import { useLocation } from 'react-router';
import { Td, Text, Th, Tr, useToken } from '@chakra-ui/react';
import { type IconName } from '@shinetools/sunshine-icons';
import { Icon, Typography } from '@shinetools/sunshine-universal';

import { type PlanGrouping } from 'features/Onboarding/modules/Pricing/types';

import { Row } from './components/Row';

type TitleRow = {
  title: string;
  iconName: IconName;
  cellsCount: number;
  planGroup?: PlanGrouping;
};

type PlanTableProps = {
  headerHeight?: number | null;
  isMobile?: boolean;
  titleRow: TitleRow;
  rows: ComponentProps<typeof Row>[];
};

export function PlanTable({
  headerHeight,
  isMobile = false,
  rows,
  titleRow,
}: PlanTableProps) {
  const location = useLocation();
  const [grey300] = useToken('colors', ['grey.300']);

  const isOnboardingFlow = location.pathname.includes('/onboarding');
  const stickyHeaderHeightPricingTable = headerHeight ?? 0;
  const stickyHeaderHeightSubscriptionTable = 145;

  const stickyHeaderHeight = isOnboardingFlow
    ? stickyHeaderHeightPricingTable
    : stickyHeaderHeightSubscriptionTable;

  return (
    <>
      {isMobile ? (
        <Tr>
          <Th
            backgroundColor="white"
            colSpan={2}
            paddingY="space-32"
            textAlign="left"
          >
            <Text as="span" display="flex" gap="space-12">
              <Icon icon={titleRow.iconName} />
              <Typography.Text bold size="regular">
                {titleRow.title}
              </Typography.Text>
            </Text>
          </Th>
        </Tr>
      ) : (
        <Tr
          // Use a boxShadow instead of a border to keep the border effect when the element gets sticky
          boxShadow={`0 1px 0 0 ${grey300}, 0 -1px 0 0 ${grey300}`}
          position="sticky"
          top={stickyHeaderHeight}
          zIndex={1}
        >
          <Th
            backgroundColor="white"
            // The overflow on the th and the wider width on the Text
            // enable the titleRow to overflow on the next cell to stay on one line
            overflow="visible"
            paddingY="space-32"
            textAlign="left"
          >
            <Text as="span" display="flex" gap="space-12" width="200%">
              <Icon icon={titleRow.iconName} />
              <Typography.Text bold size="regular">
                {titleRow.title}
              </Typography.Text>
            </Text>
          </Th>

          {/* Create the title rows cells so they can get the proper background color */}
          {Array.from({ length: titleRow.cellsCount }).map((_, index) => (
            <Td backgroundColor="white" key={index} paddingY="space-32" />
          ))}
        </Tr>
      )}

      {rows.map(({ data, subtitle, tag, title: rowTitle, tooltip }, index) => (
        <Row
          data={data}
          key={rowTitle}
          subtitle={subtitle}
          tag={tag}
          title={rowTitle}
          tooltip={tooltip}
          withoutBorder={
            (index === rows.length - 1 || index === 0) && !isMobile
          }
        />
      ))}
    </>
  );
}
